import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { Search, BookOpen, FileText, MessageSquare, BookMarked, Star, Hash, ArrowRight, LogIn, Home, X, Sparkles, BookText, Loader2, Database, Crosshair } from 'lucide-react'


interface SearchItem {
  id: string
  type: 'surah' | 'transcript' | 'knowledge' | 'action' | 'hadith' | 'bible'
  label: string
  description: string
  icon: typeof BookOpen
  action: () => void
  keywords?: string[]
}

interface SurahMeta {
  number: number
  name: string
  englishName: string
  englishNameTranslation: string
  revelationType: string
  numberOfAyahs: number
}

const QURAN_API = 'https://api.alquran.cloud/v1'
const SURAH_CACHE_KEY = 'islam-preacher-surahs'

const ACTIONS: SearchItem[] = [
  { id: 'new-chat', type: 'action', label: 'New Conversation', description: 'Start a fresh discussion', icon: MessageSquare, action: () => {} },
  { id: 'switch-learn', type: 'action', label: 'Switch to Learn Mode', description: 'Ask questions and explore theology', icon: BookOpen, action: () => {} },
  { id: 'switch-debate', type: 'action', label: 'Switch to Debate Mode', description: 'Challenge with evidence-based argumentation', icon: Crosshair, action: () => {} },
  { id: 'go-home', type: 'action', label: 'Go to Landing', description: 'Return to the home page', icon: Home, action: () => {} },
  { id: 'go-scriptures', type: 'action', label: 'Browse Scriptures', description: 'Open the full scripture browser', icon: BookText, action: () => {} },
  { id: 'go-knowledge', type: 'action', label: 'Knowledge Base', description: 'Browse raw religious texts', icon: Database, action: () => {} },
  { id: 'go-blog', type: 'action', label: 'Scholars\' Corner Blog', description: 'Read articles on theology and comparative religion', icon: FileText, action: () => {} },
  { id: 'compare', type: 'action', label: 'Cross-Reference Texts', description: 'Compare scriptures side by side', icon: Star, action: () => {} },
]

interface CommandPaletteProps {
  isOpen: boolean
  onClose: () => void
  onAction: (action: string) => void
  onSelectSurah: (number: number) => void
  onInsertText: (text: string) => void
}

export function CommandPalette({ isOpen, onClose, onAction, onSelectSurah, onInsertText }: CommandPaletteProps) {
  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [surahs, setSurahs] = useState<SurahMeta[]>([])
  const [surahsLoading, setSurahsLoading] = useState(true)
  const [activeResultType, setActiveResultType] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return
    setQuery('')
    setSelectedIndex(0)
    setActiveResultType(null)
    setTimeout(() => inputRef.current?.focus(), 50)
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    const cached = localStorage.getItem(SURAH_CACHE_KEY)
    if (cached) {
      setSurahs(JSON.parse(cached))
      setSurahsLoading(false)
      return
    }
    fetch(`${QURAN_API}/surah`)
      .then(r => r.json())
      .then(d => {
        if (d.code === 200) {
          setSurahs(d.data)
          localStorage.setItem(SURAH_CACHE_KEY, JSON.stringify(d.data))
        }
      })
      .finally(() => setSurahsLoading(false))
  }, [isOpen])

  const results = useMemo(() => {
    if (!query.trim()) return { actions: ACTIONS, surahs: [] as SearchItem[], knowledge: [] as SearchItem[], transcripts: [] as SearchItem[] }
    const q = query.toLowerCase()

    const filteredActions = ACTIONS.filter(a =>
      a.label.toLowerCase().includes(q) || a.description.toLowerCase().includes(q)
    )

    const filteredSurahs: SearchItem[] = surahs
      .filter(s =>
        s.englishName.toLowerCase().includes(q) ||
        s.name.includes(q) ||
        s.englishNameTranslation.toLowerCase().includes(q) ||
        String(s.number).includes(query)
      )
      .slice(0, 8)
      .map(s => ({
        id: `surah-${s.number}`,
        type: 'surah' as const,
        label: `${s.number}. ${s.englishName}`,
        description: `${s.englishNameTranslation} · ${s.revelationType} · ${s.numberOfAyahs} verses`,
        icon: BookOpen,
        action: () => onSelectSurah(s.number),
        keywords: [s.name, s.englishName, s.englishNameTranslation],
      }))

    const allItems = [...filteredActions, ...filteredSurahs]
    return { actions: filteredActions, surahs: filteredSurahs, knowledge: [], transcripts: [] }
  }, [query, surahs, onSelectSurah])

  const allResults = useMemo(() => {
    const items = [...results.actions, ...results.surahs, ...results.knowledge, ...results.transcripts]
    if (activeResultType) {
      return items.filter(i => i.type === activeResultType || (activeResultType === 'action' && i.type === 'action'))
    }
    return items
  }, [results, activeResultType])

  useEffect(() => {
    setSelectedIndex(0)
  }, [query, activeResultType])

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIndex(i => Math.min(i + 1, allResults.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIndex(i => Math.max(i - 1, 0)) }
    else if (e.key === 'Enter' && allResults[selectedIndex]) { allResults[selectedIndex].action(); onClose() }
    else if (e.key === 'Escape') { onClose() }
    else if (e.key === 'Tab' && e.shiftKey) {
      e.preventDefault()
      const types = ['action', 'surah', 'knowledge', 'transcript'] as const
      const currentIdx = activeResultType ? types.indexOf(activeResultType as typeof types[number]) : -1
      const nextIdx = currentIdx <= 0 ? types.length - 1 : currentIdx - 1
      setActiveResultType(types[nextIdx])
    }
    else if (e.key === 'Tab') {
      e.preventDefault()
      const types = ['action', 'surah', 'knowledge', 'transcript'] as const
      const currentIdx = activeResultType ? types.indexOf(activeResultType as typeof types[number]) : -1
      const nextIdx = currentIdx >= types.length - 1 ? 0 : currentIdx + 1
      setActiveResultType(types[nextIdx])
    }
  }, [allResults, selectedIndex, activeResultType, onClose])

  useEffect(() => {
    if (!isOpen) return
    const el = containerRef.current
    if (!el) return
    const selected = el.querySelector(`[data-index="${selectedIndex}"]`) as HTMLElement
    selected?.scrollIntoView({ block: 'nearest' })
  }, [selectedIndex, isOpen])

  if (!isOpen) return null

  const resultTypeCounts = {
    all: allResults.length,
    surah: results.surahs.length,
    action: results.actions.length,
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center pt-[12vh]" onClick={onClose}>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        ref={containerRef}
        onClick={e => e.stopPropagation()}
        className="relative w-full max-w-2xl mx-4 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] shadow-2xl overflow-hidden animate-slide-up"
      >
        <div className="flex items-center gap-3 border-b border-[hsl(var(--border))] px-4 py-3">
          <Search className="h-5 w-5 shrink-0 text-[hsl(var(--fg-muted))]" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search surahs, transcripts, knowledge base, or type a command..."
            className="flex-1 bg-transparent text-body text-[hsl(var(--fg))] outline-none placeholder:text-[hsl(var(--fg-dim))]"
          />
          {query && (
            <button onClick={() => setQuery('')} className="btn btn-ghost btn-icon shrink-0">
              <X className="h-4 w-4" />
            </button>
          )}
          <div className="hidden sm:flex items-center gap-1.5 shrink-0">
            <kbd className="rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--elevated))] px-1.5 py-0.5 text-[10px] font-mono text-[hsl(var(--fg-muted))]">esc</kbd>
          </div>
        </div>

        {!query && !activeResultType && (
          <div className="px-4 py-3 border-b border-[hsl(var(--border))]/50">
            <p className="text-caption text-[hsl(var(--fg-muted))] mb-2 uppercase tracking-wider font-medium">Quick Actions</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {ACTIONS.slice(0, 4).map(action => (
                <button
                  key={action.id}
                  onClick={() => { action.action(); onClose() }}
                  className="flex items-center gap-2 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--elevated))]/50 px-3 py-2.5 text-small text-[hsl(var(--fg))] hover:border-[hsl(var(--accent)/0.3)] hover:bg-[hsl(var(--accent-soft))] transition-all"
                >
                  <action.icon className="h-3.5 w-3.5 text-[hsl(var(--accent))] shrink-0" />
                  <span className="truncate">{action.label.replace(/^(Switch to |Go to )/, '')}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-1 border-b border-[hsl(var(--border))]/50 px-3 py-2">
          {[
            { id: null, label: `All (${resultTypeCounts.all})`, icon: Search },
            { id: 'action', label: `Actions (${resultTypeCounts.action})`, icon: Sparkles },
            { id: 'surah', label: `Surahs (${resultTypeCounts.surah})`, icon: BookOpen },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id ?? 'all'}
              onClick={() => setActiveResultType(id)}
              className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-caption font-medium transition-all ${
                activeResultType === id
                  ? 'bg-[hsl(var(--accent-soft))] text-[hsl(var(--accent))]'
                  : 'text-[hsl(var(--fg-muted))] hover:text-[hsl(var(--fg))] hover:bg-[hsl(var(--elevated))]'
              }`}
            >
              <Icon className="h-3 w-3" />
              {label}
            </button>
          ))}
        </div>

        <div className="max-h-[420px] overflow-y-auto py-2">
          {allResults.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Search className="h-8 w-8 text-[hsl(var(--fg-muted))] mb-3 opacity-40" />
              <p className="text-body text-[hsl(var(--fg))] font-medium">No results found</p>
              <p className="text-small text-[hsl(var(--fg-muted))] mt-1">Try a different search term</p>
            </div>
          )}

          {results.actions.length > 0 && (!activeResultType || activeResultType === 'action') && query && (
            <div className="px-3 py-1">
              <p className="text-caption text-[hsl(var(--fg-muted))] px-2 py-1 uppercase tracking-wider font-medium">Actions</p>
              {results.actions.map((item, i) => {
                const idx = allResults.indexOf(item)
                return (
                  <button
                    key={item.id}
                    data-index={idx}
                    onClick={() => { item.action(); onClose() }}
                    className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-all ${
                      selectedIndex === idx
                        ? 'bg-[hsl(var(--accent-soft))] text-[hsl(var(--accent))]'
                        : 'text-[hsl(var(--fg))] hover:bg-[hsl(var(--elevated))]'
                    }`}
                  >
                    <div className="flex h-7 w-7 items-center justify-center rounded-md bg-[hsl(var(--elevated))] shrink-0">
                      <item.icon className="h-3.5 w-3.5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-small font-medium truncate">{item.label}</div>
                      <div className="text-caption text-[hsl(var(--fg-muted))] truncate">{item.description}</div>
                    </div>
                    <ArrowRight className="h-3.5 w-3.5 shrink-0 opacity-0 group-hover:opacity-100" />
                  </button>
                )
              })}
            </div>
          )}

          {results.surahs.length > 0 && (!activeResultType || activeResultType === 'surah') && (
            <div className="px-3 py-1">
              <p className="text-caption text-[hsl(var(--fg-muted))] px-2 py-1 uppercase tracking-wider font-medium">Surahs</p>
              {results.surahs.map((item, i) => {
                const idx = allResults.indexOf(item)
                return (
                  <button
                    key={item.id}
                    data-index={idx}
                    onClick={() => { item.action(); onClose() }}
                    className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-all ${
                      selectedIndex === idx
                        ? 'bg-[hsl(var(--accent-soft))] text-[hsl(var(--accent))]'
                        : 'text-[hsl(var(--fg))] hover:bg-[hsl(var(--elevated))]'
                    }`}
                  >
                    <div className="flex h-7 w-7 items-center justify-center rounded-md bg-[hsl(var(--accent-soft))] shrink-0">
                      <BookOpen className="h-3.5 w-3.5 text-[hsl(var(--accent))]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-small font-medium truncate">{item.label}</div>
                      <div className="text-caption text-[hsl(var(--fg-muted))] truncate">{item.description}</div>
                    </div>
                    <Hash className="h-3 w-3 text-[hsl(var(--fg-muted))] shrink-0" />
                  </button>
                )
              })}
            </div>
          )}

          {surahsLoading && query && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-[hsl(var(--accent))]" />
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 border-t border-[hsl(var(--border))] px-4 py-2.5">
          <div className="flex items-center gap-3 text-caption text-[hsl(var(--fg-muted))]">
            <span className="flex items-center gap-1"><kbd className="rounded border border-[hsl(var(--border))] bg-[hsl(var(--elevated))] px-1 py-0.5 text-[10px] font-mono">↑↓</kbd> navigate</span>
            <span className="flex items-center gap-1"><kbd className="rounded border border-[hsl(var(--border))] bg-[hsl(var(--elevated))] px-1 py-0.5 text-[10px] font-mono">↵</kbd> select</span>
            <span className="flex items-center gap-1"><kbd className="rounded border border-[hsl(var(--border))] bg-[hsl(var(--elevated))] px-1 py-0.5 text-[10px] font-mono">esc</kbd> close</span>
            <span className="flex items-center gap-1 hidden sm:flex"><kbd className="rounded border border-[hsl(var(--border))] bg-[hsl(var(--elevated))] px-1 py-0.5 text-[10px] font-mono">Tab</kbd> filter</span>
          </div>
        </div>
      </div>
    </div>
  )
}
